import { DestroyRef, inject, Injectable, InjectionToken, signal } from '@angular/core';

import { Subject } from 'rxjs';
import { StorageFailure } from '../telemetry/enums';


export const INDEXED_DB_FACTORY = new InjectionToken<() => IDBFactory | undefined>('Signage IndexedDB', {
  providedIn: 'root', factory: () => (): IDBFactory | undefined => typeof indexedDB === 'undefined' ? undefined : indexedDB
});
export const INDEXED_DB_NAME = new InjectionToken<string>('Signage database name', {
  providedIn: 'root', factory: () => 'cubigo-signage-v1'
});

// A key/value wrapper over IndexedDB that never throws: storage can be full, blocked or disabled on a device nobody can visit.
@Injectable({ providedIn: 'root' })
export class IndexedDbStoreService {
  private readonly factory = inject(INDEXED_DB_FACTORY);
  private readonly name = inject(INDEXED_DB_NAME);
  private readonly failure = signal<StorageFailure | null>(null);
  private readonly failures = new Subject<StorageFailure>();
  private database?: IDBDatabase;
  private opening?: Promise<IDBDatabase | undefined>;
  private destroyed = false;
  private readonly transactions = new Set<IDBTransaction>();
  public readonly storageFailure = this.failure.asReadonly();
  public readonly failureEvents = this.failures.asObservable();

  public constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;

      for (const transaction of this.transactions) {
        transaction.abort();
      }

      this.database?.close();
      this.database = undefined;
      this.failures.complete();
    });
  }

  public report(failure: StorageFailure): void {
    this.failure.set(failure);
    this.failures.next(failure);
  }

  public async read(key: string, signal?: AbortSignal): Promise<unknown> {
    return this.transact('readonly', store => store.get(key), signal);
  }

  public async write(key: string, value: unknown, signal?: AbortSignal): Promise<boolean> {
    return (await this.transact('readwrite', store => store.put(value, key), signal)) !== undefined;
  }

  public async remove(key: string, signal?: AbortSignal): Promise<void> {
    await this.transact('readwrite', store => store.delete(key), signal);
  }

  public async removePrefix(prefix: string): Promise<void> {
    await this.transact('readwrite', store => store.delete(IDBKeyRange.bound(prefix, `${prefix}\uffff`)));
  }

  private async transact(
    mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest, signal?: AbortSignal
  ): Promise<unknown> {
    if (signal?.aborted || this.destroyed) {
      return undefined;
    }

    const database = await this.open();

    if (!database || signal?.aborted || this.destroyed) {
      return undefined;
    }

    return new Promise(resolve => {
      let transaction: IDBTransaction;

      try {
        transaction = database.transaction('records', mode);
        this.transactions.add(transaction);
        const abort = (): void => { transaction.abort(); };
        const finish = (result: unknown): void => {
          signal?.removeEventListener('abort', abort);
          this.transactions.delete(transaction);
          resolve(result);
        };
        signal?.addEventListener('abort', abort, { once: true });
        const request = operation(transaction.objectStore('records'));
        // Read on `oncomplete`, not on the request: only a committed transaction proves the write survived.
        transaction.oncomplete = (): void => finish(request.result);
        transaction.onabort = (): void => {
          if (!signal?.aborted && !this.destroyed) {
            this.report(mode === 'readonly' ? StorageFailure.ReadFailed : StorageFailure.WriteFailed);
          }

          finish(undefined);
        };
        // Swallowed so a failed request does not reach the global error handler; `onabort` above is what reports it.
        transaction.onerror = (event): void => { event.preventDefault(); };
      } catch {
        this.report(mode === 'readonly' ? StorageFailure.ReadFailed : StorageFailure.WriteFailed);
        resolve(undefined);
      }
    });
  }

  private open(): Promise<IDBDatabase | undefined> {
    if (this.database) {
      return Promise.resolve(this.database);
    }

    if (this.opening) {
      return this.opening;
    }

    this.opening = new Promise<IDBDatabase | undefined>(resolve => {
      try {
        const factory = this.factory();

        if (!factory || this.destroyed) {
          this.report(StorageFailure.Unavailable);
          resolve(undefined);

          return;
        }

        const request = factory.open(this.name, 1);
        let abandoned = false;
        request.onupgradeneeded = (): void => {
          if (!request.result.objectStoreNames.contains('records')) {
            request.result.createObjectStore('records');
          }
        };
        request.onsuccess = (): void => {
          const database = request.result;

          if (abandoned || this.destroyed) {
            database.close();
            resolve(undefined);

            return;
          }

          this.database = database;
          // A schema upgrade elsewhere blocks on this connection; closing lets it through and the next call reopens.
          database.onversionchange = (): void => { database.close(); this.database = undefined; };
          resolve(database);
        };
        request.onerror = (): void => { this.report(StorageFailure.Unavailable); resolve(undefined); };
        // A blocked open can still succeed after the caller gave up; `abandoned` closes that late connection.
        request.onblocked = (): void => { abandoned = true; this.report(StorageFailure.Unavailable); resolve(undefined); };
      } catch {
        this.report(StorageFailure.Unavailable);
        resolve(undefined);
      }
    }).finally(() => { this.opening = undefined; });
    return this.opening;
  }
}
